import React, { FC } from 'react';
import './BreadCrumbs.css';
import { Link, useLocation } from 'react-router-dom';
import { BREAD_CRUMPS_LABELS, toCamelCase } from '@/Shared';
import { useAppSelector } from '@/OneLiners';


interface BreadCrumbsProps { }

/**
 * =====================================================
 * BreadCrumbs
 * =====================================================
 *
 * Component displaying the navigational breadcrumb trail.
 *
 * Responsibilities:
 * - Maps the current URL path segments to human-readable labels
 * - Uses `BREAD_CRUMPS_LABELS` mapping to resolve route labels
 * - Shows the breadcrumb only if the user is online (authenticated)
 * - Provides accessible navigation via links and ARIA labels
 *
 * Author: Samano CASTRE
 * Date: July 16, 2025
 */
const BreadCrumbs: FC<BreadCrumbsProps> = () => {

	/**
	 * =====================================================
	 * Location & session state
	 * =====================================================
	 *
	 * - `location` provides the current URL path
	 * - `isOnline` ensures breadcrumbs are rendered only when the user session exists
	 */
	const location = useLocation();
	const pathnames = location.pathname.split('/').filter(Boolean);
	const isOnline = !!useAppSelector(state => state.sessionState.userSession?.id);


	/**
	 * =====================================================
	 * Crumb generation
	 * =====================================================
	 *
	 * Maps each path segment to a label. Uses `toCamelCase` to
	 * resolve keys in `BREAD_CRUMPS_LABELS`.
	 * The last segment is rendered as plain text (non-clickable).
	 */
	const crumbs = pathnames.map((segment, index) => {

		const routeTo = '/' + pathnames.slice(0, index + 1).join('/');

		// Skip root or home links
		if (routeTo === '/' || routeTo === '/home') {
			return "";
		}

		let label = decodeURIComponent(BREAD_CRUMPS_LABELS[toCamelCase(segment)] || segment);

		const isLast = index === pathnames.length - 1;

		return isLast ?
			(<span key={routeTo}>{label}</span>) :
			(<span key={routeTo}>
				<Link
					to={routeTo}
					aria-label={label}
					title={label}>
					{label}
				</Link> {' / '}
			</span>);
	});

	/**
	 * =====================================================
	 * Render
	 * =====================================================
	 *
	 * - Displays "Accueil" link at the start
	 * - Appends the dynamically generated breadcrumb trail
	 * - Only visible when the user session is online
	 */
	return (
		<nav className="BreadCrumbs ellipsis" data-testid="BreadCrumbs" aria-label="Fil d'Ariane">
			{isOnline && <>
				<Link to="/" aria-label='Accueil' title='Accueil'>Accueil</Link>
				{' / '}
				{crumbs}
			</>
			}
		</nav>
	);
}

export default BreadCrumbs;
